import { Injectable } from '@angular/core';
import { FeaturesService } from '@libraries/feature-flags';
import { LoggerService } from '@libraries/logger';
import { Environment } from '@libraries/shared-env';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilKeyChanged, filter, map } from 'rxjs/operators';
import { ImageData } from './image-registry/image-list';
import {
  ABTestImageRegistry,
  imageRegistry,
} from './image-registry/image-registry';

@Injectable({
  providedIn: 'root',
})
export class ImagesService {
  // Console log styles. Had to have some fun.
  bold = 'font-weight: bold; font-style: italic;';
  registry: ABTestImageRegistry;

  private abTestUpdateSubject = new BehaviorSubject<string>(null);
  public abTestUpdate$ = this.abTestUpdateSubject.asObservable();

  constructor(
    private features: FeaturesService,
    private env: Environment,
    private logger: LoggerService
  ) {
    this.features.featureUpdate$.subscribe((abTestName) =>
      this.abTestUpdateSubject.next(abTestName)
    );
    this.useRegistry(imageRegistry);
  }

  get$(image: string, abTestName?: string): Observable<ImageData> {
    return this.abTestUpdate$.pipe(
      map((_) => this.get(image, abTestName)),
      filter((img) => Boolean(img)),
      distinctUntilKeyChanged('imageUrl')
    );
  }

  get(image: string, abTestName?: string): ImageData {
    if (!this.registry[image]) {
      this.logger.warn(
        `Cannot find %c"${image}"%c. Ensure the image is registered and check the name.`,
        this.bold,
        ''
      );
      return null;
    }

    // When no test is specified, just use control as the default
    if (!abTestName) return this.registry[image].control;

    // Otherwise, we must be running an a/b test
    // The feature flag for the test needs to be set remotely to true or false
    // Until that happens we'll be returning null to avoid showing any image
    const abVersion = this.features.isOn(abTestName);
    const abTestResolved = abVersion === true || abVersion === false;
    if (!abTestResolved) return null;

    // Determine which version of the test we're on and get it from the registry
    const imageVariant = abVersion ? 'variation' : 'control';
    const imageObject = this.registry[image][imageVariant];

    // This may happen if you forgot to add a "variation" to your image in the image registry
    if (!imageObject) {
      this.logger.warn(
        `Missing %c"${imageVariant}"%c version for %c"${image}"%c.`,
        this.bold,
        '',
        this.bold,
        ''
      );
      return null;
    }

    // You've jumped through the nine rings of hell and can show an image
    return imageObject;
  }

  useRegistry(registry: ABTestImageRegistry) {
    this.registry = registry;
  }
}
